import { createFileRoute, Link } from "@tanstack/react-router";
import portraitAsset from "@/assets/a-propos.webp.asset.json";

export const Route = createFileRoute("/energeticienne-toulouse")({
  head: () => ({
    meta: [
      { title: "Énergéticienne à Toulouse — Caroline Balech | Rénergescence" },
      { name: "description", content: "Caroline Balech, énergéticienne à Toulouse. Approche globale, magnétisme et soins énergétiques en cabinet ou à distance. Prenez rendez-vous." },
      { property: "og:title", content: "Énergéticienne à Toulouse — Rénergescence" },
      { property: "og:description", content: "Soins énergétiques, magnétisme et approche holistique à Toulouse." },
      { property: "og:url", content: "/energeticienne-toulouse" },
      { property: "og:image", content: portraitAsset.url },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/energeticienne-toulouse" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "LocalBusiness",
              "@id": "https://renergescence.com/#localbusiness",
              name: "Rénergescence",
              image: portraitAsset.url,
              url: "https://renergescence.com/energeticienne-toulouse",
              telephone: "+33689676546",
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                streetAddress: "3 rue Ledru-Rollin",
                addressLocality: "Toulouse",
                postalCode: "31300",
                addressCountry: "FR",
              },
              areaServed: [
                { "@type": "City", name: "Toulouse" },
              ],
            },
            {
              "@type": "Person",
              name: "Caroline Balech",
              jobTitle: "Énergéticienne, praticienne en soins énergétiques et naturopathe",
              worksFor: { "@id": "https://renergescence.com/#localbusiness" },
              image: portraitAsset.url,
              areaServed: { "@type": "City", name: "Toulouse" },
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                { "@type": "Question", name: "Qu'est-ce qu'une énergéticienne ?", acceptedAnswer: { "@type": "Answer", text: "Une énergéticienne accompagne le rééquilibrage des énergies du corps et de l'esprit par l'imposition des mains, le magnétisme et des protocoles énergétiques adaptés à chaque personne." } },
                { "@type": "Question", name: "Où se déroulent les séances à Toulouse ?", acceptedAnswer: { "@type": "Answer", text: "Les séances se tiennent au cabinet, 3 rue Ledru-Rollin, 31300 Toulouse. Elles peuvent aussi avoir lieu à distance, avec la même efficacité." } },
                { "@type": "Question", name: "Combien de temps dure une séance ?", acceptedAnswer: { "@type": "Answer", text: "Comptez environ 1 heure pour une première séance, échange et soin compris." } },
                { "@type": "Question", name: "Faut-il croire pour que cela fonctionne ?", acceptedAnswer: { "@type": "Answer", text: "Non. L'énergétique agit indépendamment des croyances. Une posture d'ouverture suffit pour accueillir les effets de la séance." } },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const soins = [
  { name: "Soin énergétique global", desc: "Rééquilibrage profond des centres énergétiques.", to: "/prestations" as const, hash: "soins-energetiques" },
  { name: "Magnétisme", desc: "Apaisement par imposition des mains, en cabinet ou à distance.", to: "/prestations" as const, hash: "soins-energetiques" },
  { name: "Massage énergétique", desc: "Travail manuel et énergétique combiné.", to: "/massage-energetique-toulouse" as const },
  { name: "Naturopathie", desc: "Bilan et programme d'hygiène de vie personnalisé.", to: "/prestations" as const, hash: "naturopathie" },
];

const faqs = [
  { q: "Qu'est-ce qu'une énergéticienne ?", a: "Une énergéticienne accompagne le rééquilibrage des énergies du corps et de l'esprit par l'imposition des mains, le magnétisme et des protocoles énergétiques adaptés à chaque personne." },
  { q: "Où se déroulent les séances à Toulouse ?", a: "Les séances se tiennent au cabinet, 3 rue Ledru-Rollin, 31300 Toulouse. Elles peuvent aussi avoir lieu à distance, avec la même efficacité." },
  { q: "Combien de temps dure une séance ?", a: "Comptez environ 1 heure pour une première séance, échange et soin compris." },
  { q: "Faut-il croire pour que cela fonctionne ?", a: "Non. L'énergétique agit indépendamment des croyances. Une posture d'ouverture suffit pour accueillir les effets de la séance." },
];

const testimonials = [
  { n: "Marine D. — Avis Google", t: "Caroline est une praticienne exceptionnelle, à l'écoute et bienveillante. Je sors de chaque séance plus légère et apaisée. Je recommande vivement son cabinet à Toulouse." },
  { n: "Julien P. — Avis Google", t: "Une véritable parenthèse de douceur. Le cadre est apaisant et Caroline a su mettre le doigt sur ce dont j'avais besoin. Mon sommeil s'est nettement amélioré depuis." },
  { n: "Camille R. — Avis Google", t: "Très belle découverte. Caroline est à la fois professionnelle et profondément humaine. Une séance qui fait beaucoup de bien au corps comme à l'esprit." },
];

function Page() {
  return (
    <>
      <section className="pt-20 pb-12 md:pt-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="eyebrow">Toulouse · À distance</p>
          <h1 className="mt-5 font-display text-4xl font-light italic md:text-6xl">Énergéticienne à Toulouse</h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed md:text-lg" style={{ color: "var(--color-muted-foreground)" }}>
            Caroline Balech vous accompagne au cabinet Rénergescence à Toulouse. Approche globale, magnétisme et soins énergétiques sur mesure pour retrouver vitalité, équilibre et sérénité.
          </p>
          <Link to="/contact" className="mt-8 inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
            Prendre rendez-vous
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="mx-auto grid max-w-[1100px] gap-12 px-6 md:grid-cols-[360px_1fr] md:items-center">
          <div className="overflow-hidden rounded-3xl">
            <img src={portraitAsset.url} alt="Caroline Balech, énergéticienne à Toulouse" width={720} height={900} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-light md:text-4xl">Une approche globale du soin énergétique</h2>
            <p className="mt-5 text-base leading-relaxed" style={{ color: "var(--color-text)" }}>
              Guidée par vos énergies et à l'écoute de mes ressentis intérieurs, j'accompagne chaque personne dans sa singularité. Aussi, mes connaissances en santé naturelle acquises à l'E.I.B.E de Toulouse me permettent d'avoir une vision globale et pluridimensionnelle de chacun. La séance vise à libérer les blocages énergétiques, soutenir le terrain et restaurer l'équilibre intérieur.
            </p>
            <ul className="mt-6 space-y-3 text-[15px]" style={{ color: "var(--color-text)" }}>
              <li>• Stress, anxiété, charge mentale</li>
              <li>• Fatigue chronique, manque de vitalité</li>
              <li>• Douleurs et tensions corporelles</li>
              <li>• Transitions de vie, accompagnement émotionnel</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/prestations" hash="soins-energetiques" className="rounded-full border px-5 py-2 text-sm" style={{ borderColor: "#7A8A76", color: "var(--color-sage-text)" }}>Voir les soins</Link>
              <Link to="/tarifs" className="rounded-full px-5 py-2 text-sm text-white" style={{ backgroundColor: "#7A8A76" }}>Tarifs</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Les soins proposés à Toulouse</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {soins.map((s) => (
              <Link key={s.name} to={s.to} hash={(s as { hash?: string }).hash} className="rounded-3xl border bg-white p-6 transition-shadow hover:shadow-md" style={{ borderColor: "#e8e0d8" }}>
                <h3 className="font-display text-lg" style={{ color: "#2D2B28" }}>{s.name}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--color-text)" }}>{s.desc}</p>
                <p className="mt-4 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--color-sage-text)" }}>En savoir plus →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="mx-auto grid max-w-[1100px] gap-10 px-6 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-display text-3xl font-light md:text-4xl">Où me trouver à Toulouse&nbsp;?</h2>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>
              Le cabinet se situe en plein cœur de Toulouse, dans un espace calme et accessible, à proximité immédiate des transports en commun.
            </p>
            <address className="mt-6 not-italic text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>
              <strong>Cabinet de Toulouse</strong><br />
              3 rue Ledru-Rollin<br />
              31300 Toulouse<br />
              <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>Métro Patte d'Oie · accès PMR</span>
            </address>
            <p className="mt-4 text-[14px]" style={{ color: "var(--color-muted-foreground)" }}>
              Téléphone : <a href="tel:+33689676546" className="underline">06 89 67 65 46</a>
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "#e8e0d8" }}>
            <iframe
              title="Plan d'accès au cabinet Rénergescence à Toulouse"
              src="https://www.google.com/maps?q=3+rue+Ledru-Rollin+31300+Toulouse&output=embed"
              width="100%"
              height="320"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Ils témoignent</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.n} className="rounded-3xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <blockquote className="font-display text-lg italic leading-snug" style={{ color: "#2D2B28" }}>« {t.t} »</blockquote>
                <figcaption className="mt-4 text-xs uppercase tracking-[0.18em]" style={{ color: "#6a6258" }}>{t.n}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[820px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Questions fréquentes</h2>
          <dl className="mt-10 space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <dt className="font-display text-lg" style={{ color: "#2D2B28" }}>{f.q}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>{f.a}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 text-center">
            <Link to="/contact" className="inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
              Réserver ma séance
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}